import React from 'react';
import type { Account, SavedCandidate, User } from '@axiom/validation';
import {
  Card,
  CardSection,
  CardFooter,
  Button,
  Well,
  ImageCircle,
  Layout,
  CandidateProfileUtil,
  ParagraphHeader,
  Icon,
  CondensedLarge,
  Gutter,
  CondensedHeader,
  useApi,
  LightTheme,
  TaxonomyUtil,
  IconButton,
  BusinessTitlesUtil,
} from '@axiom/ui';
import { PossibleImageSizes } from '@axiom/const';
import { UrlSlugsUtil } from '@axiom/utils';

import { TaxonomyApi } from '../../api/taxonomy-api';
import { TalentCarouselBadge } from '../TalentCarouselBadge/TalentCarouselBadge';
import { CandidateApi } from '../../api/candidate-api';

export type SavedTalentCarouselCardProps = {
  account: Account;
  candidate: SavedCandidate['candidate'];
  user: User;
  setInquiredCandidate: React.Dispatch<
    React.SetStateAction<SavedCandidate['candidate'] | null>
  >;
};

export const SavedTalentCarouselCard = ({
  account,
  candidate,
  setInquiredCandidate,
}: SavedTalentCarouselCardProps) => {
  const { industryValue } = account;
  const [{ data: taxonomy }] = useApi(TaxonomyApi.readTaxonomy());
  const { industries } = taxonomy;

  const findIndustry = () => {
    const industry = candidate.industries?.find(
      ind => ind.industryValue === industryValue
    );
    const maxIndustry = candidate.industries?.sort(
      (a, b) => +b.yearsOfExperience - +a.yearsOfExperience
    )[0];
    return industry ?? maxIndustry;
  };

  const industryDisplayText = () => {
    const industry = findIndustry();
    const industryYOE = industry?.yearsOfExperience;
    const industryName = industry?.industryValue
      ? industries[industry.industryValue]?.displayValue
      : null;
    return !industryYOE || Math.round(+industryYOE) === 0
      ? `Less than 1 Year in ${industryName}`
      : `${Math.round(+industryYOE)} Years in ${industryName}`;
  };

  const candidateExperienceDisplayText = () => {
    const candidateYOE = candidate.yearsOfExperience || 0;
    const oneYearValue = 1;
    const twentyYearValue = 20;
    if (candidateYOE < oneYearValue) {
      return 'Less than 1 Year of experience';
    } else if (candidateYOE && candidateYOE >= twentyYearValue) {
      return '20+ Years of experience';
    }
    return `${Math.round(candidateYOE)} Years of experience`;
  };

  const handleSaveTalentClick = async () => {
    await CandidateApi.deleteSavedCandidate(candidate.id);
    await CandidateApi.refreshFavoriteCandidates();
  };

  const formattedLegalRole = BusinessTitlesUtil.locationConversion({
    businessTitle:
      TaxonomyUtil.getBusinessTitle(taxonomy, candidate.businessTitle)
        ?.displayName ?? null,
    candidate,
  });

  return (
    <Card name="SAVED_TALENT_CAROUSEL_CARD" stretched>
      <CardSection divider>
        <Well background="light blue">
          <Layout horizontalGutter="16px" position="top">
            <div>
              <ImageCircle
                name="SAVED_TALENT_IMAGE"
                size="small"
                background="blue"
                imageName={candidate.calculatedDisplayName}
                src={CandidateProfileUtil.getProfileImageUri(
                  candidate,
                  PossibleImageSizes.W_100
                )}
              />
            </div>
            <Layout direction="vertical" verticalGutter="8px" stretched>
              <ParagraphHeader name="SAVED_TALENT_NAME">
                {candidate.calculatedDisplayName}
              </ParagraphHeader>
              <LightTheme backgroundName="none">
                <TalentCarouselBadge candidate={candidate} isSavedCarousel />
              </LightTheme>
            </Layout>
            <div>
              <IconButton
                name="REMOVE_SAVED_TALENT"
                icon="star-filled"
                pattern="secondary"
                variation="minimal"
                onClick={handleSaveTalentClick}
              />
            </div>
          </Layout>
          <Gutter bottom="16px" />
          <Layout verticalGutter="16px" direction="vertical">
            <Layout horizontalGutter="16px">
              <Icon name="checkmark" />
              <CondensedLarge name="SAVED_CANDIDATE_EXPERIENCE">
                {candidateExperienceDisplayText()}
              </CondensedLarge>
            </Layout>
            {(candidate.industries?.length ?? 0) > 0 && (
              <Layout horizontalGutter="16px">
                <Icon name="checkmark" />
                <CondensedLarge name="SAVED_INDUSTRY_EXPERIENCE">
                  {industryDisplayText()}
                </CondensedLarge>
              </Layout>
            )}
          </Layout>
        </Well>
        <Gutter bottom="16px" />
        <CondensedHeader>Primary Practice Area</CondensedHeader>
        <CondensedLarge name="SAVED_PRACTICE_AREA">
          {TaxonomyUtil.formatPracticeAreaFocusAreaForDisplay(
            taxonomy,
            candidate.primaryPracticeArea,
            candidate.primaryFocusArea
          )}
        </CondensedLarge>
        {formattedLegalRole ? (
          <Gutter vertical="16px">
            <CondensedHeader>Legal Role</CondensedHeader>
            <CondensedLarge name="SAVED_LEGAL_ROLE">
              {formattedLegalRole}
            </CondensedLarge>
          </Gutter>
        ) : null}
      </CardSection>
      <CardFooter>
        <Button
          pattern="secondary"
          variation="outline"
          toTab={`/browse-talent/${UrlSlugsUtil.generateCandidateSlug(candidate, taxonomy)}`}
          name="VIEW_TALENT_BUTTON"
        >
          View Talent
        </Button>
        <Button
          name="REQUEST_INFO_BUTTON"
          pattern="secondary"
          onClick={() => {
            setInquiredCandidate(candidate);
          }}
        >
          Contact Axiom
        </Button>
      </CardFooter>
    </Card>
  );
};
